import React, { type FC, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import cookies from 'js-cookie'
import { Button } from '@mobi/component-library/Base/Buttons'
import { colors, font } from '@mobi/component-library/Foundations'
import { Constants } from '@mobi/settings'
import { useServerConfig } from '@core/Data/ServerConfig/useServerConfig'
import { clearCookieValue } from '@core/Utils/cookies'

export const VersionOverrideIndicator: FC = () => {
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null)
  const [isDismissed, setIsDismissed] = useState(false)

  const serverConfig = useServerConfig()
  const isInternal = serverConfig?.isInternalRequest ?? false

  useEffect(() => {
    const versionCookie = cookies.get(Constants.WebAppVersionCookieName)
    setSelectedVersion(versionCookie || null)
  }, [])

  const onRevert = () => {
    clearCookieValue(Constants.WebAppVersionCookieName)
    window.location.reload()
  }

  const onDismiss = () => setIsDismissed(true)

  const isVisible = Boolean(selectedVersion) && !isDismissed

  if (!isVisible || !selectedVersion || !isInternal) return null

  return (
    <TopBannerVersionIndicatorStyled role='status' aria-live='polite'>
      <span>{selectedVersion}</span>
      <Button
        color='primary_destructive'
        size='xxs'
        aria-label='Revert to default version'
        onClick={onRevert}
      >
        &times; Revert
      </Button>
      <Button color='primary' size='xxs' aria-label='Dismiss banner' onClick={onDismiss}>
        &times; Dismiss
      </Button>
    </TopBannerVersionIndicatorStyled>
  )
}

const TopBannerVersionIndicatorStyled = styled.div({
  position: 'static',
  maxWidth: '80rem',
  margin: '0 auto',
  minHeight: '50px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.75rem',
  background: 'linear-gradient(135deg, #7c3aed, #15ad25)',
  color: colors.neutral[100],
  ...font.size.md,

  '& > span': {
    flex: '1 1 auto',
    minWidth: 0,
    ...font.size.md,
    fontWeight: font.weight.bold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '0.7rem 0.9rem',
  },

  '& button': {
    flexShrink: 0,
    marginRight: '0.75rem',
    color: `${colors.neutral[100]} !important`,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: '0.1rem solid rgba(255, 255, 255, 0.2)',

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        border: '0.1rem solid rgba(255, 255, 255, 0.3)',
      },
    },
  },
})
